<template>
    <Modal :closeOnBackdrop="false" @close="$emit('close')">
      <div slot="body">
        {{ $t('after_save_reaction') }}
        <br><br>
        <!-- Check if recommendations array is empty -->
        <template v-if="items.length === 0">
          {{ $t('no_recommendations') }}
        </template>

        <!-- Loop through recommendations and render clickable links -->
        <template v-else>
          <ul>
            <li v-for="(item, index) in items" :key="index">
              <a
                :href="generateAmazonLink(item)"
                target="_blank"
                rel="sponsored noopener"
                @click="trackClick(item, index)"
              >
                {{ item }}
              </a>
            </li>
          </ul>
          <!-- Required by section 5 of the Amazon Associates Operating Agreement:
               the disclosure must appear wherever Special Links are shown. -->
          <p class="affiliate-disclosure">{{ $t('amazon_disclosure') }}</p>
        </template>
      </div>

    </Modal>
</template>

<script>
import Modal from './Modal.vue'

// One entry per Amazon marketplace we hold an Associates tag for. ES and FR have
// to be registered separately (afiliados.amazon.es, partenaires.amazon.fr); until
// they exist they deliberately point at the US entry, because an untagged
// amazon.es link earns nothing with certainty, which is worse than the status quo.
const MARKETPLACES = {
  es: { host: 'www.amazon.com', tag: 'legi010-20' }, // TODO: www.amazon.es once the ES tag exists
  fr: { host: 'www.amazon.com', tag: 'legi010-20' }, // TODO: www.amazon.fr once the FR tag exists
  en: { host: 'www.amazon.com', tag: 'legi010-20' }
}

// Amazon search-index values. 'stripbooks' covers all books, physical and digital.
// Do NOT default to 'digital-text' (Kindle): pairing it with an ES/FR reader on the
// US storefront lands them on territory-locked editions they cannot buy.
const SEARCH_INDEX = { book: 'stripbooks', kindle: 'digital-text', audible: 'audible' }

export default {
  name: 'Recommendations',
  components: {
    Modal
  },
  data () {
    return {}
  },
  props: {
    name: { type: String, default: '' },
    parent: { type: Object, default: null },
    recommendations: { type: Array, default: () => [] },
    // The reading language the user actually chose, not navigator.language.
    langValue: { type: String, default: 'es-ES' },
    storyId: { type: [String, Number], default: '' },
    reaction: { type: String, default: '' }
  },

  computed: {
    items () {
      return Array.isArray(this.recommendations) ? this.recommendations : []
    }
  },

  methods: {
    marketplace () {
      const lang = String(this.langValue || 'es-ES').slice(0, 2)
      return MARKETPLACES[lang] || MARKETPLACES.en
    },

    // The backend returns display strings shaped "Title (Author)".
    parseItem (item) {
      const m = /^(.*?)\s*\(([^()]*)\)\s*$/.exec(String(item || ''))
      return m
        ? { title: m[1].trim(), author: m[2].trim() }
        : { title: String(item || '').trim(), author: '' }
    },

    // Normalises instead of mutilating. The previous regex used \w, which is
    // ASCII-only in JavaScript, so "L'Étranger" became "LÉtranger", "Tell-Tale"
    // became "TellTale" and non-Latin titles were emptied entirely -- every one
    // of those a dead Amazon search.
    searchQuery (item) {
      const { title, author } = this.parseItem(item)
      return `${title} ${author}`
        .normalize('NFC')
        .replace(/[‘’]/g, "'")
        .replace(/[–—]/g, ' ')
        .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },

    generateAmazonLink (item, kind = 'book') {
      const { host, tag } = this.marketplace()
      const params = new URLSearchParams({
        k: this.searchQuery(item),
        i: SEARCH_INDEX[kind] || SEARCH_INDEX.book,
        tag: tag
      })
      return `https://${host}/s?${params.toString()}`
    },

    trackClick (item, index) {
      try {
        const { title, author } = this.parseItem(item)
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'affiliate_click',
          affiliate_network: 'amazon',
          marketplace: this.marketplace().host,
          book_title: title,
          book_author: author,
          slot: index,
          source_story_id: this.storyId,
          reaction: this.reaction,
          ui_locale: this.langValue
        })
      } catch (e) {
        // Analytics must never block the outbound click.
        console.error(e)
      }
    }
  }
}
</script>

<style scoped>

.buttons {
  -webkit-border-radius: 6;
  -moz-border-radius: 6;
  border-radius: 6px;
  font-family: Arial;
  color: #00073F;
  font-size: 20px;
  padding: 10px 20px 10px 20px;
  border: solid #00073F 2px;
  text-decoration: none;
  width: 30vh;
  margin: 1vh
}

button:hover {
  text-decoration: none;
}

li {
  display: block;
  list-style: circle;
  text-align: left;
  padding-bottom: 3%;
  font-weight: bold;
}

.affiliate-disclosure {
  font-size: 0.78rem;
  font-weight: 400;
  opacity: 0.75;
  text-align: left;
  margin: 14px 0 0;
  line-height: 1.45;
}
</style>
